/**
 * checkIn write op — POSTs to /api/projects/calllog/ to create a new
 * CallLog row when a rep checks into a store. First op in the
 * offline-first migration (#1983); template for the other 13 ops.
 *
 * applyLocal writes a row to visit_task_sessions with callLogId=null
 * and status='in_progress'. After successful sync, reconcile stamps
 * the server's calllogid onto that row, which downstream dependent
 * ops (checkOut, submitSurvey, uploadPhoto, etc.) then resolve via.
 */

import type { WriteOp, SyncResult } from '../types';
import { STORES, put, getAll } from '../../utils/indexedDB';
import { BASE_URL, jsonHeaders, classifyResponse, networkErrorResult } from '../httpHelpers';
import { emit } from '../../utils/eventLog';
import { toNaiveLocal } from '../../utils/visitTime';

export interface CheckInInput {
  storeId: number; // legacystoreid (used for routing)
  projectId: number;
  associateId: number;
  storeName: string;
  gps?: { lat: number; lng: number; accuracy?: number };
  odometer?: number;
  /**
   * Drive-start odometer for the odometer-only check-in (no drive segment).
   * The server rejects a lone driveodoend ("Both Start and End miles must be
   * entered together"), so this pairs with `odometer` as driveodostart. The
   * running odometer before the check-in reading; 0 when unknown.
   */
  startOdometer?: number;
  dteCall: string; // YYYY-MM-DD
  stCallTime: string; // YYYY-MM-DDTHH:MM:SS
  driveData?: {
    startTime: string;
    endTime: string;
    startOdometer?: number;
    endOdometer?: number;
  };
  gpsOverrideReason?: string;
  /**
   * Why this visit exists on a store/project the rep's schedule doesn't carry.
   * Its own CallLog column — an audit fact must not ride notes, which checkout
   * rewrites wholesale.
   */
  offScheduleReason?: string;
}

export interface CheckInResponse {
  calllogid: number;
}

export const checkInOp: WriteOp<CheckInInput, CheckInInput, CheckInResponse> = {
  kind: 'checkIn',

  async applyLocal(input, localId) {
    await put(STORES.VISIT_TASK_SESSIONS, {
      uuid: localId,
      callLogId: null,
      status: 'in_progress',
      storeId: input.storeId,
      projectId: input.projectId,
      associateId: input.associateId,
      storeName: input.storeName,
      startTime: input.stCallTime,
      lat: input.gps?.lat ?? null,
      lng: input.gps?.lng ?? null,
      gpsAccuracy: input.gps?.accuracy ?? null,
      startOdometer: input.odometer ?? null,
      gpsOverrideReason: input.gpsOverrideReason ?? null,
    });

    // Event stream (#1950): record the check-in as a store_checked_in event so
    // the synced/total "Visits" count is exact. The previous gap meant a
    // check-in created a CallLog but emitted NO event. Single chokepoint —
    // applyLocal runs once per check-in regardless of UI entry point.
    // Best-effort: a failed emit must never break the check-in write.
    try {
      await emit({
        event_type: 'store_checked_in',
        event_at: input.stCallTime,
        store_id: input.storeId,
        project_id: input.projectId,
        payload: { local_id: localId, gps_override: input.gpsOverrideReason ?? null },
      });
    } catch {
      /* event emit is best-effort */
    }
  },

  serialize(input, localId) {
    return {
      action: 'checkIn',
      endpoint: '/api/projects/calllog/',
      method: 'POST',
      payload: input,
      priority: 10, // check-ins drain first
      maxRetries: 10, // losing a check-in is unacceptable
      dependsOn: null,
      relatedTaskUUID: localId,
      idempotencyKey: localId,
    };
  },

  async sync(row): Promise<SyncResult> {
    const input = row.payload as CheckInInput;
    const body: Record<string, unknown> = {
      store_id: input.storeId,
      project_id: input.projectId,
      associate_id: input.associateId,
      dtecall: input.dteCall,
      stcalltime: input.stCallTime,
      teamproject: false,
      teamleader: false,
    };
    if (input.gpsOverrideReason) body.gps_override_reason = input.gpsOverrideReason;
    if (input.offScheduleReason) body.off_schedule_reason = input.offScheduleReason;
    if (input.gps) {
      body.lat = Math.round(input.gps.lat * 1_000_000) / 1_000_000;
      body.lon = Math.round(input.gps.lng * 1_000_000) / 1_000_000;
      // Round accuracy to 2 places to match the server DecimalField(max_digits=10,
      // decimal_places=2). A raw iOS accuracy float (e.g. 35.123456789) is 11+ total
      // digits and DRF validates digit count BEFORE rounding, rejecting the whole
      // check-in sync ("no more than 10 digits in total"). #2958 / diag #69.
      if (input.gps.accuracy != null)
        body.gps_accuracy = Math.round(input.gps.accuracy * 100) / 100;
    }
    if (input.driveData) {
      body.stdrivetime = toNaiveLocal(input.driveData.startTime);
      body.eddrivetime = toNaiveLocal(input.driveData.endTime);
      if (input.driveData.startOdometer !== undefined)
        body.driveodostart = input.driveData.startOdometer;
      if (input.driveData.endOdometer !== undefined)
        body.driveodoend = input.driveData.endOdometer;
    } else if (input.odometer !== undefined) {
      // Odometer entered at check-in with no drive segment. The server
      // rejects a lone driveodoend ("Both Start and End miles must be entered
      // together"), so send the drive-start odo too. Mirrors the legacy
      // direct-POST payload the dual-write era relied on (#1987).
      body.driveodoend = input.odometer;
      body.driveodostart = input.startOdometer ?? 0;
    }

    let response: Response;
    try {
      response = await fetch(`${BASE_URL}${row.endpoint}`, {
        method: row.method,
        headers: jsonHeaders(row.idempotencyKey),
        body: JSON.stringify(body),
      });
    } catch (err) {
      return networkErrorResult(err);
    }

    const classified = await classifyResponse(response);
    if (classified) return classified;

    const data = await response.json();
    const callLog = data.data || data;
    if (!callLog || typeof callLog.calllogid !== 'number') {
      return {
        status: 'failed',
        retriable: false,
        errorMessage: 'Server response missing calllogid',
      };
    }
    return { status: 'success', serverId: callLog.calllogid, retriable: false };
  },

  async reconcile(row, response) {
    const localId = row.relatedTaskUUID;
    if (!localId) return;
    const rows = (await getAll(STORES.VISIT_TASK_SESSIONS)) as Array<Record<string, unknown>>;
    const match = rows.find((r) => r.uuid === localId);
    if (!match) return;
    await put(STORES.VISIT_TASK_SESSIONS, { ...match, callLogId: response.calllogid });
  },

  dependsOn() {
    return null;
  },
};

export type CheckInOp = typeof checkInOp;
